import { type AskPunttChatUserSummary } from "@mg/schemas/src/christo/askPuntt";
import { Plus, X } from "@phosphor-icons/react";
import {
  Avatar,
  Button,
  Dialog,
  Flex,
  IconButton,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useEffect, useMemo, useState } from "react";

import { useUI } from "../../../contexts/ui";
import {
  useSearchEnterpriseUsers,
  useShareAskPunttChat,
  useUnshareAskPunttChat,
} from "../../../utils/queries/askPuntt";
import { sortEnterpriseUsers } from "../../../utils/sortEnterpriseUsers";

interface ShareAskPunttChatDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Called when the dialog is dismissed. */
  onOpenChange: (open: boolean) => void;
  /** Chat being shared. */
  chatId: string;
  /** Title shown above the dialog body. */
  chatTitle?: string;
  /** Currently shared users (the chat's allowList, hydrated). */
  members: AskPunttChatUserSummary[];
}

/**
 * Owner-facing dialog for sharing an Ask Puntt chat with other enterprise
 * users. Drives the server-side `searchEnterpriseUsers` endpoint so the
 * picker doesn't pull the full enterprise roster, and uses
 * `shareChat` / `unshareChat` mutations to update the chat's allowList in
 * place. Mirrors the access-grant pattern used by tickets/folders, but
 * scoped to existing enterprise users (no email/JWT-link path in v1).
 */
export function ShareAskPunttChatDialog({
  open,
  onOpenChange,
  chatId,
  chatTitle,
  members,
}: ShareAskPunttChatDialogProps) {
  const { notify } = useUI();
  const [rawQuery, setRawQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Local 250ms debounce so each keystroke doesn't trigger a server call.
  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(rawQuery), 250);
    return () => window.clearTimeout(handle);
  }, [rawQuery]);

  // Reset state whenever the dialog opens fresh.
  useEffect(() => {
    if (open) {
      setRawQuery("");
      setDebouncedQuery("");
    }
  }, [open]);

  const trimmedQuery = debouncedQuery.trim();
  const search = useSearchEnterpriseUsers(trimmedQuery, {
    enabled: open && trimmedQuery.length >= 1,
  });
  const shareMutation = useShareAskPunttChat(chatId);
  const unshareMutation = useUnshareAskPunttChat(chatId);

  const memberIds = useMemo(
    () => new Set(members.map((m) => m._id)),
    [members],
  );

  const candidates = useMemo(
    () =>
      sortEnterpriseUsers(
        (search.data?.users ?? []).filter((u) => !memberIds.has(u._id)),
      ),
    [search.data, memberIds],
  );

  const handleAdd = async (user: AskPunttChatUserSummary) => {
    try {
      await shareMutation.mutateAsync([user._id]);
      notify({
        title: "Chat shared",
        message: `Shared with ${user.name || user.email || "user"}.`,
      });
      setRawQuery("");
      setDebouncedQuery("");
    } catch {
      // useShareAskPunttChat already shows an error toast; swallow here so
      // the dialog stays open and the user can retry.
    }
  };

  const handleRemove = async (user: AskPunttChatUserSummary) => {
    try {
      await unshareMutation.mutateAsync(user._id);
      notify({
        title: "Removed",
        message: `Removed ${user.name || user.email || "user"} from this chat.`,
      });
    } catch {
      // Mutation hook already shows an error toast.
    }
  };

  const renderUserMeta = (user: AskPunttChatUserSummary) => {
    const display = user.name?.trim() || user.email?.trim() || user._id;
    const subtitle = user.name && user.email ? user.email : undefined;
    return (
      <Flex direction="column" className="min-w-0 flex-1">
        <Text size="2" weight="medium" className="truncate">
          {display}
        </Text>
        {subtitle ? (
          <Text size="1" color="gray" className="truncate">
            {subtitle}
          </Text>
        ) : null}
      </Flex>
    );
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        maxWidth="500px"
        data-testid="ask-puntt-share-chat-dialog"
        aria-labelledby="ask-puntt-share-chat-title"
      >
        <Dialog.Title id="ask-puntt-share-chat-title">Share chat</Dialog.Title>
        <Dialog.Description size="2" mb="3">
          {chatTitle
            ? `Give other people in your organization access to "${chatTitle}". They'll be able to read messages and reply.`
            : "Give other people in your organization access to this chat. They'll be able to read messages and reply."}
        </Dialog.Description>

        <Text
          as="label"
          size="2"
          weight="medium"
          htmlFor="ask-puntt-share-search-input"
        >
          Search people
        </Text>
        <TextField.Root
          id="ask-puntt-share-search-input"
          data-testid="ask-puntt-share-search-input"
          placeholder="Search by name or email"
          value={rawQuery}
          onChange={(e) => setRawQuery(e.target.value)}
          mt="1"
          autoComplete="off"
        />

        {trimmedQuery.length >= 1 ? (
          <Flex
            direction="column"
            gap="1"
            mt="2"
            data-testid="ask-puntt-share-results"
            role="list"
            aria-label="Search results"
          >
            {search.isLoading ? (
              <Text
                size="2"
                color="gray"
                data-testid="ask-puntt-share-results-loading"
              >
                Searching…
              </Text>
            ) : candidates.length === 0 ? (
              <Text
                size="2"
                color="gray"
                data-testid="ask-puntt-share-results-empty"
              >
                No new matches for &quot;{trimmedQuery}&quot;.
              </Text>
            ) : (
              candidates.map((user) => (
                <Flex
                  key={user._id}
                  align="center"
                  gap="2"
                  data-testid={`ask-puntt-share-result-${user._id}`}
                  role="listitem"
                  className="rounded-md px-2 py-1.5 hover:bg-puntt-neutral-gray-3"
                >
                  <Avatar
                    src={user.avatar ?? undefined}
                    fallback={(user.name || user.email || "?")
                      .charAt(0)
                      .toUpperCase()}
                    size="1"
                    radius="full"
                  />
                  {renderUserMeta(user)}
                  <IconButton
                    data-testid={`ask-puntt-share-add-${user._id}`}
                    aria-label={`Share with ${user.name || user.email || "user"}`}
                    variant="soft"
                    size="1"
                    disabled={shareMutation.isPending}
                    onClick={() => handleAdd(user)}
                  >
                    <Plus size={14} aria-hidden="true" />
                  </IconButton>
                </Flex>
              ))
            )}
          </Flex>
        ) : null}

        <Flex direction="column" gap="1" mt="4">
          <Text size="2" weight="medium" id="ask-puntt-share-members-heading">
            People with access
          </Text>
          {members.length === 0 ? (
            <Text
              size="2"
              color="gray"
              data-testid="ask-puntt-share-members-empty"
            >
              Only you. Search above to share with teammates.
            </Text>
          ) : (
            <Flex
              direction="column"
              gap="1"
              role="list"
              aria-labelledby="ask-puntt-share-members-heading"
            >
              {members.map((user) => (
                <Flex
                  key={user._id}
                  align="center"
                  gap="2"
                  data-testid={`ask-puntt-share-member-${user._id}`}
                  role="listitem"
                  className="rounded-md px-2 py-1.5"
                >
                  <Avatar
                    src={user.avatar ?? undefined}
                    fallback={(user.name || user.email || "?")
                      .charAt(0)
                      .toUpperCase()}
                    size="1"
                    radius="full"
                  />
                  {renderUserMeta(user)}
                  <IconButton
                    data-testid={`ask-puntt-share-remove-${user._id}`}
                    aria-label={`Remove ${user.name || user.email || "user"} from shared list`}
                    variant="soft"
                    color="gray"
                    size="1"
                    disabled={unshareMutation.isPending}
                    onClick={() => handleRemove(user)}
                  >
                    <X size={14} aria-hidden="true" />
                  </IconButton>
                </Flex>
              ))}
            </Flex>
          )}
        </Flex>

        <Flex mt="4" justify="end">
          <Button
            variant="soft"
            color="gray"
            data-testid="ask-puntt-share-cancel-button"
            onClick={() => onOpenChange(false)}
          >
            Done
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
